import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  CreditCard, Search, Download, CheckCircle, Clock, 
  AlertCircle, Send, FileText, DollarSign, Users, XCircle 
} from 'lucide-react';
import { payrollAPI } from '../../../services/api';
import { toast } from 'sonner';

const BankTransfers = () => {
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [transfersRes, runsRes] = await Promise.all([
        payrollAPI.getBankTransfers(),
        payrollAPI.getRuns({ status: 'CHECK_RESULTS' })
      ]);
      setTransfers(transfersRes.data || []);
      setRuns(runsRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load bank transfers');
    } finally {
      setLoading(false);
    }
  };

  const filteredTransfers = transfers.filter(transfer => {
    const matchesSearch = 
      (transfer.transfer_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (transfer.payroll_run_number || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || transfer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleGenerateTransfer = async (runId) => {
    try {
      await payrollAPI.generateBankTransfer(runId);
      toast.success('Bank transfer generated');
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to generate transfer');
      }
    }
  };

  const handleSubmitTransfer = async (transferId) => {
    try {
      await payrollAPI.submitBankTransfer(transferId, 'ADMIN');
      toast.success('Transfer submitted');
      loadData();
    } catch (error) {
      toast.error('Failed to submit transfer');
    }
  };

  const handleMarkProcessed = async (transferId) => {
    try {
      await payrollAPI.markTransferProcessed(transferId);
      toast.success('Transfer marked as processed');
      loadData();
    } catch (error) {
      toast.error('Failed to update transfer');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getStatusConfig = (status) => {
    const configs = {
      'PENDING': { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      'GENERATED': { color: 'bg-orange-100 text-orange-700', icon: FileText },
      'SUBMITTED': { color: 'bg-orange-100 text-orange-700', icon: Send },
      'PROCESSED': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'FAILED': { color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    return configs[status] || configs['PENDING'];
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="bank-transfers-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/payroll')}>Payroll</span>
            <span>/</span>
            <span>Bank Transfers</span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bank Transfers</h1>
              <p className="text-gray-600 mt-1">Manage bank transfer batches for salary payments</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-xl font-semibold text-yellow-600">
                  {transfers.filter(t => t.status === 'PENDING').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Send className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Submitted</p>
                <p className="text-xl font-semibold text-orange-600">
                  {transfers.filter(t => t.status === 'SUBMITTED').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Processed</p>
                <p className="text-xl font-semibold text-green-600">
                  {transfers.filter(t => t.status === 'PROCESSED').length}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Amount (Pending)</p>
                <p className="text-xl font-semibold">
                  {formatCurrency(transfers.filter(t => t.status !== 'PROCESSED').reduce((sum, t) => sum + (t.total_amount || 0), 0))}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Generate from Payroll Runs */}
        {runs.length > 0 && (
          <Card className="p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Generate Bank Transfer from Approved Runs</h3>
            <div className="flex flex-wrap gap-2">
              {runs.map(run => (
                <Button
                  key={run.id}
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerateTransfer(run.id)}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {run.run_number}
                </Button>
              ))}
            </div>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search by transfer number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="PROCESSED">Processed</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>

        {/* Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredTransfers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No bank transfers found. Generate transfers from approved payroll runs.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Transfer #</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Payroll Run</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Period</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Transfer Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Employees</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Amount</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTransfers.map((transfer) => {
                    const statusConfig = getStatusConfig(transfer.status);
                    return (
                      <tr key={transfer.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-orange-600">{transfer.transfer_number}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{transfer.payroll_run_number}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{transfer.period_description}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{transfer.transfer_date}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4 text-gray-400" />
                            {transfer.total_employees}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                          {formatCurrency(transfer.total_amount)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>
                            <statusConfig.icon className="h-3 w-3" />
                            {transfer.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setSelectedTransfer(transfer)}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            {transfer.status === 'PENDING' && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-orange-600"
                                onClick={() => handleSubmitTransfer(transfer.id)}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            {transfer.status === 'SUBMITTED' && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-green-600"
                                onClick={() => handleMarkProcessed(transfer.id)}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Transfer Details Modal */}
        {selectedTransfer && (
          <TransferDetailsModal 
            transfer={selectedTransfer} 
            onClose={() => setSelectedTransfer(null)} 
          />
        )}
      </div>
    </Layout>
  );
};

// Transfer Details Modal
const TransferDetailsModal = ({ transfer, onClose }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Transfer Details</h2>
            <p className="text-gray-600">{transfer.transfer_number}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <XCircle className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">Total Amount</p>
              <p className="text-lg font-bold text-gray-900">{formatCurrency(transfer.total_amount)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Employees</p>
              <p className="text-lg font-bold text-gray-900">{transfer.total_employees}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Transfer Date</p>
              <p className="text-lg font-bold text-gray-900">{transfer.transfer_date}</p>
            </div>
          </div>

          {/* Transfer Items */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Payment Details</h3>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Employee</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Bank</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Account</th>
                    <th className="px-4 py-2 text-right text-sm font-medium text-gray-600">Amount</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transfer.transfer_items?.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2 text-sm">
                        <div>
                          <p className="font-medium">{item.employee_name}</p>
                          <p className="text-gray-500 text-xs">{item.employee_id}</p>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700">{item.bank_name}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">****{item.account_number?.slice(-4)}</td>
                      <td className="px-4 py-2 text-sm text-right font-medium">{formatCurrency(item.amount)}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          item.status === 'Processed' ? 'bg-green-100 text-green-700' :
                          item.status === 'Failed' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button>
            <Download className="h-4 w-4 mr-2" />
            Download File
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BankTransfers;
